import { Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const rows = [
  {
    aspect: 'Curriculum Alignment',
    generic: 'You manually prompt for NCERT topics with no guarantee of accuracy',
    xed21: 'Built-in NCERT/CBSE curriculum tree — select board, grade, subject, chapter',
  },
  {
    aspect: "Bloom's Taxonomy",
    generic: 'You describe cognitive levels yourself; tagging is inconsistent',
    xed21: 'One-click Bloom\'s level selection (Remember → Create) on every question',
  },
  {
    aspect: 'Question Format',
    generic: 'Free-text output you copy-paste and reformat manually',
    xed21: '6 structured formats with proper option layout, ready to use',
  },
  {
    aspect: 'Answer Keys & Explanations',
    generic: 'Sometimes included, sometimes not; no standard format',
    xed21: 'Always generated — correct answer marked, detailed explanation included',
  },
  {
    aspect: 'Editing',
    generic: 'Re-prompt from scratch to change one option or fix a distractor',
    xed21: 'Inline editing — modify text, options, difficulty without regenerating',
  },
  {
    aspect: 'Assessment Bundling',
    generic: 'You manually compile questions into a document',
    xed21: 'Bundle into timed assessments with point values and export',
  },
  {
    aspect: 'Pricing',
    generic: 'Monthly subscription ($20+/mo) whether you use it or not',
    xed21: 'Pay-per-question from ₹5. No subscription, no expiry',
  },
];

export default function ComparisonSection() {
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Why Not Just Use{' '}
            <span className="text-slate-400">ChatGPT?</span>
          </h2>
          <p className="text-lg text-slate-500 mt-4">
            Generic AI chats can generate questions, but they weren't built
            for Indian classrooms.
          </p>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left font-semibold text-slate-500 px-6 py-4 w-[28%]">
                  Feature
                </th>
                <th className="text-left font-semibold text-slate-400 px-6 py-4 w-[36%]">
                  Generic AI Chat
                </th>
                <th className="text-left font-semibold text-teal-700 px-6 py-4 w-[36%]">
                  Xed21
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.aspect}
                  className={i < rows.length - 1 ? 'border-b border-slate-100' : ''}
                >
                  <td className="px-6 py-4 font-medium text-slate-900 align-top">
                    {row.aspect}
                  </td>
                  <td className="px-6 py-4 text-slate-400 align-top">
                    <span className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" />
                      {row.generic}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-700 align-top">
                    <span className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      {row.xed21}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-4">
          {rows.map((row) => (
            <div
              key={row.aspect}
              className="rounded-xl border border-slate-200 overflow-hidden"
            >
              <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
                <span className="text-sm font-semibold text-slate-900">
                  {row.aspect}
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                <div className="px-4 py-3 flex items-start gap-2">
                  <X className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-400">{row.generic}</span>
                </div>
                <div className="px-4 py-3 flex items-start gap-2 bg-teal-50/40">
                  <Check className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{row.xed21}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 text-white rounded-xl text-sm font-semibold hover:bg-teal-800 transition-all shadow-sm hover:shadow-md"
          >
            Try Xed21 Free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
